// SUB-OFFICE 7 — ghost petitioners, now articulated. One InstancedMesh per
// figure part (head, torso, limbs) sharing a single paper-grey material; every
// ghost is posed each frame through one scratch FigureRig (procedural walk
// cycle: leg swing, arm counter-swing, torso lean, idle sway). Population
// tracks the live traffic figure from /api/stats (min 3, cap 12 this slice).
// During a FULL adjudication every ghost stops and turns to face the
// petitioner inside a 300ms window with per-ghost stagger — a murmuration of
// clerks. Consumed by boot.ts (adds this.group to the scene).

import {
  BoxGeometry, Color, DynamicDrawUsage, Group, InstancedMesh, Matrix4,
  MeshLambertMaterial,
} from 'three';
import { PAL } from '../palette';
import { WAYPOINTS } from '../../shared/physspec';
import { patchMaterial } from '../render/scene';
import { FIGURE_PART_NAMES, FigureRig, figureGeometries, type FigurePart } from './figure';
import type { Entity, EventBus } from './entities';
import type { Player } from './player';
import type { StatsBlob } from '../../shared/types';

const CAP = 12;
const MIN = 3;
const SPEED = 1.8 * 0.7; // ghosts shuffle at 0.7x player speed (approx)
const STRIDE_RATE = 11 / 1.8; // walk-cycle phase per meter-ish, matches player feel

const LANES: Array<[number, number]> = WAYPOINTS.map((w) => [31 - w.x, 17 - w.y]);
const ADJ: number[][] = WAYPOINTS.map((w) =>
  w.adj.map((id) => WAYPOINTS.findIndex((x) => x.id === id)).filter((i) => i >= 0)
);

interface Ghost {
  active: boolean;
  x: number;
  z: number;
  wp: number;
  tx: number;
  tz: number;
  yaw: number;
  offx: number;
  offz: number;
  pauseS: number;
  turnDelay: number;
  phase: number; // walk-cycle phase
  stride: number; // 0..1 blend into the stride
  idleOff: number; // per-ghost idle clock offset
  speedMul: number; // per-ghost gait speed (some shuffle, some hurry)
  carries: boolean; // ~40% carry a manila folder in the left hand
}

export class Ghosts implements Entity {
  readonly id = 'ghosts';
  readonly group = new Group();

  private parts: Record<FigurePart, InstancedMesh>;
  private folders: InstancedMesh; // 11th part: a manila folder, ~40% of ghosts
  private folderHidden = new Matrix4().makeTranslation(0, -80, 0);
  private rig = new FigureRig();
  private ghosts: Ghost[] = [];
  private target = 5;
  private frozen = false;
  private t = 0;

  constructor(private bus: EventBus, private player: Player) {
    const mat = new MeshLambertMaterial({
      color: new Color(PAL.ghost).convertSRGBToLinear(), flatShading: true,
    });
    patchMaterial(mat);
    const geos = figureGeometries();
    this.parts = {} as Record<FigurePart, InstancedMesh>;
    for (const name of FIGURE_PART_NAMES) {
      const m = new InstancedMesh(geos[name], mat, CAP);
      m.instanceMatrix.setUsage(DynamicDrawUsage);
      m.frustumCulled = false;
      this.parts[name] = m;
      this.group.add(m);
    }

    // the held folder: one InstancedMesh in manila, posed at the left hand of
    // the ~40% who carry — the crowd reads as staff moving paper, not mannequins
    const folderGeo = new BoxGeometry(0.22, 0.3, 0.03);
    folderGeo.rotateX(-0.35);
    folderGeo.translate(0.05, -0.34, 0.08); // ride the left lower-arm hand
    const folderMat = new MeshLambertMaterial({
      color: new Color(PAL.manila).convertSRGBToLinear(), flatShading: true,
    });
    patchMaterial(folderMat);
    // local offset baked into the geo; the instance matrix is the hand's world
    this.folders = new InstancedMesh(folderGeo, folderMat, CAP);
    this.folders.instanceMatrix.setUsage(DynamicDrawUsage);
    this.folders.frustumCulled = false;
    this.group.add(this.folders);

    for (let i = 0; i < CAP; i++) {
      const wp = Math.floor(Math.random() * LANES.length);
      const [x, z] = LANES[wp];
      this.ghosts.push({
        active: i < this.target,
        x, z, wp, tx: x, tz: z, yaw: Math.random() * Math.PI * 2,
        offx: (Math.random() - 0.5) * 0.8,
        offz: (Math.random() - 0.5) * 0.8,
        pauseS: Math.random() * 3,
        turnDelay: Math.random() * 0.3, // the 300ms murmuration window
        phase: Math.random() * Math.PI * 2,
        stride: 0,
        idleOff: Math.random() * 40,
        speedMul: 0.8 + Math.random() * 0.45, // some shuffle, some hurry
        carries: i % 5 < 2, // deterministic ~40% carry a folder
      });
      this.retarget(this.ghosts[i]);
    }

    bus.on('stats', (p) => {
      const s = p as StatsBlob;
      const traffic = typeof s.live_traffic === 'number' ? s.live_traffic : 0;
      this.target = Math.max(MIN, Math.min(CAP, MIN + Math.floor(traffic / 2)));
    });
    bus.on('ceremony:adjudication', (p) => {
      if ((p as { tier: string }).tier === 'FULL') this.frozen = true;
    });
    bus.on('ceremony:verdict', () => {
      this.frozen = false;
    });
  }

  private retarget(g: Ghost): void {
    const options = ADJ[g.wp];
    if (!options || !options.length) return;
    const next = options[Math.floor(Math.random() * options.length)];
    g.wp = next;
    g.tx = LANES[next][0] + g.offx;
    g.tz = LANES[next][1] + g.offz;
  }

  /** Write active ghost xz pairs into out (flat [x,z] pairs); returns count.
   *  Consumed by render/shadows.ts — the blob discs follow the shuffle. */
  collectShadowXZ(out: Float32Array): number {
    let n = 0;
    for (let i = 0; i < CAP && n * 2 + 1 < out.length; i++) {
      const g = this.ghosts[i];
      if (!g || !g.active) continue;
      out[n * 2] = g.x;
      out[n * 2 + 1] = g.z;
      n++;
    }
    return n;
  }

  /** Pose the scratch rig as ghost i and write its part matrices. */
  private writeGhost(i: number, g: Ghost): void {
    this.rig.root.position.set(g.x, g.active ? 0 : -60, g.z);
    this.rig.root.rotation.set(0, g.yaw, 0);
    this.rig.pose({
      phase: g.phase,
      speed01: g.stride,
      idleT: this.t + g.idleOff,
    });
    this.rig.root.updateMatrixWorld(true);
    for (const name of FIGURE_PART_NAMES) {
      this.parts[name].setMatrixAt(i, this.rig.nodes[name].matrixWorld);
    }
    // the folder rides the left lower-arm's world transform (offset baked in);
    // non-carriers and inactive ghosts stash it far below the floor
    this.folders.setMatrixAt(
      i, g.active && g.carries ? this.rig.nodes.armLL.matrixWorld : this.folderHidden
    );
  }

  update(dt: number): void {
    this.t += dt;
    for (let i = 0; i < CAP; i++) {
      const g = this.ghosts[i];
      g.active = i < this.target;
      let walking = false;
      if (g.active) {
        if (this.frozen) {
          // stop and turn to face the petitioner (staggered, then held)
          g.turnDelay -= dt;
          if (g.turnDelay <= 0) {
            g.yaw = Math.atan2(this.player.pos.x - g.x, this.player.pos.z - g.z);
          }
        } else {
          g.turnDelay = Math.random() * 0.3;
          if (g.pauseS > 0) {
            g.pauseS -= dt;
          } else {
            const dx = g.tx - g.x;
            const dz = g.tz - g.z;
            const d = Math.hypot(dx, dz);
            if (d < 0.25) {
              // most pauses are brief; ~1 in 4 is a long dwell — a ghost stops
              // to read a posting and file a thought, and the crowd gets texture
              g.pauseS = Math.random() < 0.25 ? 5 + Math.random() * 4 : 0.5 + Math.random() * 3;
              this.retarget(g);
            } else {
              const step = Math.min(d, SPEED * g.speedMul * dt);
              g.x += (dx / d) * step;
              g.z += (dz / d) * step;
              g.yaw = Math.atan2(dx, dz);
              walking = true;
            }
          }
        }
      }
      // stride blend + phase advance (legs settle instead of freezing mid-swing)
      g.stride = walking ? Math.min(1, g.stride + dt * 5) : Math.max(0, g.stride - dt * 5);
      if (g.stride > 0.01) g.phase += dt * SPEED * STRIDE_RATE * (0.8 + 0.2 * g.stride) * 2.2;
      this.writeGhost(i, g);
    }
    for (const name of FIGURE_PART_NAMES) {
      this.parts[name].instanceMatrix.needsUpdate = true;
    }
    this.folders.instanceMatrix.needsUpdate = true;
  }
}
